import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Eyebrow } from "@/components/Eyebrow";
import { Reveal } from "@/components/Reveal";
import { ScaleReveal } from "@/components/ScaleReveal";
import { Mark } from "@/components/Mark";
import Journey from "@/components/Journey";
import Compare from "@/components/Compare";

const Label = ({ children }) => (
  <p className="text-xs font-medium uppercase tracking-[0.28em] text-mirian-wine/50">
    {children}
  </p>
);

const Question = ({ children, testId }) => (
  <Reveal>
    <p
      data-testid={testId}
      className="font-serif text-2xl italic leading-snug text-mirian-wine sm:text-3xl lg:text-4xl"
    >
      {children}
    </p>
  </Reveal>
);

const Concept = ({ title, children, testId }) => (
  <Reveal>
    <div
      data-testid={testId}
      className="bg-mirian-wine px-8 py-12 text-mirian-cream md:px-14 md:py-16"
    >
      <p className="font-serif text-3xl uppercase leading-tight tracking-wide sm:text-4xl">
        {title}
      </p>
      <div className="mt-8 max-w-2xl text-sm leading-relaxed text-mirian-cream/70 md:text-base">
        {children}
      </div>
    </div>
  </Reveal>
);

const ShiftConcept = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-30% 0px" });
  const on = reduce || inView;

  return (
    <Reveal>
      <div
        ref={ref}
        data-testid="mirada-salazones-concepto"
        className="bg-mirian-wine px-8 py-12 text-mirian-cream md:px-14 md:py-16"
      >
        <p className="font-serif text-3xl uppercase leading-tight tracking-wide sm:text-4xl">
          <span className="relative inline-block">
            <motion.span
              className="inline-block"
              initial={false}
              animate={on ? { opacity: 0.35 } : { opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.9 }}
            >
              De explicar el producto
            </motion.span>
            <motion.span
              aria-hidden="true"
              className="absolute left-0 top-1/2 h-[2px] w-full origin-left bg-mirian-cream/60"
              initial={false}
              animate={on ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ duration: 0.5, delay: 0.9 }}
              style={{ scaleX: 0 }}
            />
          </span>{" "}
          <motion.span
            className="inline-block"
            initial={false}
            animate={on ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
            transition={{ duration: 0.7, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
            style={reduce ? undefined : { opacity: 0 }}
          >
            a <Mark underline delay={2.1}>
              facilitar la elección
            </Mark>
            .
          </motion.span>
        </p>
        <motion.div
          className="mt-8 max-w-2xl text-sm leading-relaxed text-mirian-cream/70 md:text-base"
          initial={false}
          animate={on ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.8, delay: 1.9 }}
          style={reduce ? undefined : { opacity: 0 }}
        >
          <p>No significa dejar de comunicar tradición, producto u origen.</p>
          <p className="mt-4">
            Significa añadir una capa pensada específicamente para quien
            todavía no tiene los códigos de la categoría. Una comunicación que
            reduzca incertidumbre, genere referencias y ayude a construir poco
            a poco el criterio que el consumidor habitual ya posee.
          </p>
        </motion.div>
      </div>
    </Reveal>
  );
};

const SeqLine = ({ children, delay = 0, className = "" }) => (
  <motion.p
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-10% 0px" }}
    transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.p>
);

export default function Miradas() {
  return (
    <section
      data-testid="miradas-section"
      className="px-6 py-28 md:px-12 md:py-40 lg:px-20"
    >
      <div className="mx-auto max-w-6xl">
        <Eyebrow index="02" label="Una pequeña mirada" />
        <Reveal delay={0.1}>
          <h2 className="mt-10 font-serif text-4xl leading-[1.1] sm:text-5xl">
            Una pequeña mirada.
          </h2>
        </Reveal>
        <div className="mt-12 max-w-xl">
          <Reveal delay={0.2}>
            <p className="text-lg leading-relaxed text-mirian-wine/80">
              Una cosa es contaros cómo pienso.
            </p>
          </Reveal>
          <Reveal delay={0.5}>
            <p className="mt-3 font-serif text-2xl italic text-mirian-wine">
              Otra es dejar que lo veáis.
            </p>
          </Reveal>
          <div className="mt-10 space-y-5 text-base leading-relaxed text-mirian-wine/75">
            <Reveal y={20}>
              <p>
                Para hacerlo, elegí dos negocios con los que ya trabajáis y me
                permití mirarlos durante un rato.
              </p>
            </Reveal>
            <Reveal delay={0.2} y={20}>
              <p>
                No conozco sus objetivos, sus datos ni todo lo que ocurre
                detrás de cada proyecto. Así que esto no pretende decir qué
                haría diferente.
              </p>
            </Reveal>
            <Reveal delay={0.35} y={20}>
              <p>
                Es simplemente un pequeño ejercicio para enseñaros dónde se va
                mi cabeza cuando miro un negocio.
              </p>
            </Reveal>
          </div>
          <div className="mt-8 space-y-2 font-serif text-xl italic text-mirian-wine/85 sm:text-2xl">
            <SeqLine>Lo que veo.</SeqLine>
            <SeqLine delay={0.35}>Lo que me hace preguntarme.</SeqLine>
            <SeqLine delay={0.7}>
              Y dónde puede llevarme una de esas preguntas.
            </SeqLine>
          </div>
        </div>

        {/* MIRADA 01 — MÁRMOLES SAN JAVIER */}
        <article data-testid="mirada-marmoles" className="mt-28 border-t-2 border-mirian-wine pt-10 md:mt-36">
          <Reveal>
            <Label>Una mirada / Mármoles San Javier</Label>
          </Reveal>
          <ScaleReveal delay={0.1}>
            <h3 className="mt-6 max-w-3xl font-serif text-3xl leading-[1.15] sm:text-4xl lg:text-5xl">
              ¿Y si una parte del valor ocurre{" "}
              <Mark>antes de tocar la piedra</Mark>?
            </h3>
          </ScaleReveal>

          <div className="mt-10 max-w-xl text-base leading-relaxed text-mirian-wine/75">
            <Reveal y={20}>
              <p>
                Al mirar Mármoles San Javier entendí que el resultado final es
                solo una parte de lo que compra el cliente.
              </p>
            </Reveal>
            <Reveal delay={0.35} y={20}>
              <p className="mt-5">Antes hay una idea.</p>
            </Reveal>
            <Reveal delay={0.15} y={20}>
              <p className="mt-5">
                Y entre esa idea y el resultado existe algo menos visible:
              </p>
            </Reveal>
            <Reveal delay={0.6} y={12}>
              <p className="mt-4 font-serif text-xl italic text-mirian-wine sm:text-2xl">
                <span className="border-b border-mirian-wine/40 pb-0.5">
                  interpretación.
                </span>
              </p>
            </Reveal>
            <Reveal delay={0.2} y={20}>
              <p className="mt-8">
                El cliente puede llegar sabiendo perfectamente qué necesita.
              </p>
            </Reveal>
            <Reveal delay={0.25} y={20}>
              <p className="mt-5">
                Pero también puede llegar con una referencia, una sensación o
                una imagen que simplemente dice: «Quiero algo así.»
              </p>
            </Reveal>
            <ScaleReveal delay={0.1}>
              <p className="mt-8">
                Y ahí el valor profesional no consiste únicamente en fabricar.
                Consiste en traducir una intención en decisiones posibles.
              </p>
            </ScaleReveal>
          </div>

          <div className="mt-14 md:mt-20">
            <Reveal>
              <p className="text-sm text-mirian-wine/60">Eso me hizo preguntarme:</p>
            </Reveal>
            <div className="mt-4">
              <Reveal delay={0.45} y={44}>
                <p
                  data-testid="mirada-marmoles-pregunta"
                  className="font-serif text-2xl italic leading-snug text-mirian-wine sm:text-3xl lg:text-4xl"
                >
                  «¿Y si parte de la comunicación pudiera{" "}
                  <Mark delay={1.2}>hacer visible</Mark> esa capacidad de
                  interpretación?»
                </p>
              </Reveal>
            </div>
          </div>

          <Reveal>
            <div className="mt-12 max-w-xl space-y-5 text-base leading-relaxed text-mirian-wine/75">
              <p>No solamente enseñar qué sabe hacer Mármoles San Javier.</p>
              <p>
                También ayudar al cliente a comprender todo lo que un
                profesional es capaz de ver dentro de una idea que para él
                todavía está incompleta.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 md:mt-20">
            <Journey />
          </div>

          <Concept
            testId="mirada-marmoles-concepto"
            title={
              <>
                De{" "}
                <Mark underline delay={0.5}>
                  la idea a la decisión
                </Mark>
                .
              </>
            }
          >
            <p>
              Referencias. Dudas. Elecciones de material. Posibilidades.
              Limitaciones. Criterios. Decisiones.
            </p>
            <p className="mt-4">
              Todo lo que convierte un{" "}
              <em className="font-serif text-mirian-cream">«me gusta esto»</em>{" "}
              en un proyecto que realmente puede existir.
            </p>
          </Concept>

          <Reveal>
            <p className="mt-12 max-w-xl text-base leading-relaxed text-mirian-wine/75">
              No lo plantearía como una campaña cerrada sin conocer objetivos
              ni datos. Lo veo como una dirección que exploraría porque puede
              hacer visible una parte del valor que normalmente permanece
              detrás del resultado.
            </p>
          </Reveal>
        </article>

        {/* MIRADA 02 — SALAZONES DIEGO */}
        <article data-testid="mirada-salazones" className="mt-28 border-t-2 border-mirian-wine pt-10 md:mt-36">
          <Reveal>
            <Label>Otra mirada / Salazones Diego</Label>
          </Reveal>
          <ScaleReveal delay={0.1}>
            <h3 className="mt-6 max-w-3xl font-serif text-3xl leading-[1.15] sm:text-4xl lg:text-5xl">
              ¿Qué cambia cuando quieres vender una tradición a{" "}
              <Mark>alguien que todavía no forma parte de ella</Mark>?
            </h3>
          </ScaleReveal>

          <div className="mt-10 max-w-xl text-base leading-relaxed text-mirian-wine/75">
            <Reveal y={20}>
              <p>
                Mirando Salazones Diego me encontré con una situación
                diferente.
              </p>
            </Reveal>
            <Reveal delay={0.2} y={20}>
              <p className="mt-5">
                Para alguien acostumbrado al salazón, palabras como mojama,
                hueva de mújol o maruca tienen significado.
              </p>
            </Reveal>
          </div>
          <div className="mt-6 max-w-xl space-y-1.5 font-serif text-lg italic text-mirian-wine/85">
            <SeqLine delay={0.1}>Hay referencias.</SeqLine>
            <SeqLine delay={0.28}>Hay hábitos.</SeqLine>
            <SeqLine delay={0.46}>
              Hay una forma aprendida de elegir y consumir.
            </SeqLine>
          </div>
          <div className="mt-12 max-w-xl text-base leading-relaxed text-mirian-wine/75">
            <Reveal delay={0.35} y={20}>
              <p>
                Pero si uno de los objetivos es llegar a nuevos públicos,
                aparece otra cuestión:
              </p>
            </Reveal>
            <Reveal delay={0.2} y={20}>
              <p className="mt-5">
                el conocimiento que facilita la compra al consumidor habitual
                puede no existir todavía en el nuevo.
              </p>
            </Reveal>
          </div>

          <Compare />

          <div className="py-16 md:py-24">
            <ScaleReveal>
              <p className="font-serif text-2xl italic text-mirian-wine sm:text-3xl">
                Y eso cambia la comunicación.
              </p>
            </ScaleReveal>
          </div>

          <div>
            <Reveal>
              <p className="text-sm text-mirian-wine/60">Me hizo preguntarme:</p>
            </Reveal>
            <div className="mt-4">
              <Question testId="mirada-salazones-pregunta">
                «¿La primera tarea es enseñarle más sobre el producto o{" "}
                <Mark delay={0.6}>hacerle más fácil entrar en la categoría</Mark>?»
              </Question>
            </div>
          </div>

          <ScaleReveal>
            <p className="mt-16 max-w-xl text-base leading-relaxed text-mirian-wine/75">
              Porque antes de querer conocer diferencias, procedencias o
              procesos, puede existir una barrera mucho más básica:{" "}
              <em className="font-serif text-xl text-mirian-wine sm:text-2xl">
                «No sé qué elegir.»
              </em>
            </p>
          </ScaleReveal>

          <div className="mt-14 md:mt-20">
            <ShiftConcept />
          </div>

          <Reveal>
            <p className="mt-12 max-w-xl text-base leading-relaxed text-mirian-wine/75">
              Si el objetivo es conquistar nuevos públicos sin perder la
              esencia, quizá parte del reto no esté en cambiar el producto.
            </p>
          </Reveal>
          <ScaleReveal delay={0.15}>
            <p
              data-testid="mirada-salazones-cierre"
              className="mt-6 max-w-3xl font-serif text-3xl leading-[1.15] sm:text-4xl lg:text-5xl"
            >
              Está en construir{" "}
              <Mark delay={0.5}>la puerta de entrada</Mark>.
            </p>
          </ScaleReveal>
        </article>

        {/* CIERRE DE LAS DOS MIRADAS */}
        <div data-testid="miradas-cierre" className="mt-32 md:mt-48">
          <ScaleReveal>
            <p className="font-serif text-3xl leading-snug sm:text-4xl">
              Dos negocios.
            </p>
          </ScaleReveal>
          <ScaleReveal delay={0.2}>
            <p className="mt-4 font-serif text-3xl leading-snug text-mirian-wine/60 sm:text-4xl">
              Dos preguntas completamente diferentes.
            </p>
          </ScaleReveal>

          <div className="mt-14 max-w-xl space-y-6">
            <Reveal delay={0.1}>
              <div className="flex items-baseline gap-6 border-t border-mirian-wine/15 pt-5">
                <span className="w-40 shrink-0 text-xs uppercase tracking-[0.2em] text-mirian-wine/50">
                  Mármoles
                </span>
                <span className="font-serif text-xl italic sm:text-2xl">
                  Idea → interpretación
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="flex items-baseline gap-6 border-t border-mirian-wine/15 pt-5">
                <span className="w-40 shrink-0 text-xs uppercase tracking-[0.2em] text-mirian-wine/50">
                  Salazones
                </span>
                <span className="font-serif text-xl italic sm:text-2xl">
                  Conocimiento → elección
                </span>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <p className="mt-14 max-w-xl text-base leading-relaxed text-mirian-wine/75">
              En uno, mi mirada se fue al valor que existe entre una idea y su
              ejecución. En el otro, a la distancia entre conocer un producto y
              sentirse capaz de elegirlo.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-16 max-w-xl text-base text-mirian-wine/70 md:text-lg">
              No buscaba encontrarles una campaña.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl font-serif text-2xl italic leading-snug text-mirian-wine/80 sm:text-3xl">
              Buscaba enseñaros algo mucho más sencillo:
            </p>
          </Reveal>
          <ScaleReveal delay={0.25}>
            <p className="mt-12 max-w-4xl font-serif text-3xl leading-[1.2] sm:text-4xl lg:text-5xl">
              cómo intento entender un negocio{" "}
              <Mark delay={0.55}>antes de decidir qué comunicar</Mark>.
            </p>
          </ScaleReveal>
        </div>
      </div>
    </section>
  );
}
